import PropTypes from 'prop-types';
import { useState, useEffect, memo, useMemo } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Avatar, Box, Drawer, IconButton, Link, SpeedDial, SpeedDialAction, Stack, Typography, useMediaQuery } from '@mui/material';
import { KeyboardArrowLeftOutlined, Logout } from '@mui/icons-material';

// third-party
import PerfectScrollbar from 'react-perfect-scrollbar';

// project imports
import { WORKPLACE_PATH } from 'config';
import { useWorkspace } from 'contexts/WorkspaceContext';
import UpperMenuList from './UpperMenuList';
import MenuList from './MenuList';
import LogoSection from '../LogoSection';
import Workspace from './Workspace';
import { usePermissions } from 'contexts/PermissionContext';
import { openDrawer } from 'store/slices/menu';
import { getWorkspaces } from 'store/slices/workspace';
import { useDispatch, useSelector } from 'store';
import { drawerWidth } from 'store/constant';
import Chip from 'ui-component/extended/Chip';

import { IconInfinity } from '@tabler/icons';
import projectLogo from 'assets/images/project-logo.png';
import tokenIcon from 'assets/images/token-icon-L.png';

import useAuth from 'hooks/useAuth';

import ProjectList from './ProjectList';

// ==============================|| SIDEBAR DRAWER ||============================== //

const Sidebar = ({ window, drawerOpen, handleToggleDrawer }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { updateWorkspaceId } = useWorkspace();
    const { workspaceId, projectId } = useParams();
    const matchUpLg = useMediaQuery(theme.breakpoints.up('lg'));
    const { tokenCount } = useSelector((state) => state.profile);
    const { logout, user } = useAuth();
    const { hasPermission } = usePermissions();

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(
            getWorkspaces()
        );
    }, [dispatch]);
    
    const logo = useMemo(
        () => (
            <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
                <Box sx={{ display: 'flex', p: 2, mx: 'auto' }}>
                    <LogoSection path={projectId != undefined? `/w/${workspaceId}/p/${projectId}` : `/w/${workspaceId}`}/>
                </Box>
            </Box>
        ),
        []
    );

    const handleChangeWorkspace = (val) => {
        updateWorkspaceId(val);
        navigate(`/w/${val}`)
    }

    const drawer = useMemo(
        () => (
            <PerfectScrollbar
                component="div"
                style={{
                    width: `${drawerWidth}px`,
                    height: 'calc(100vh - 312px)',
                    padding: '0 16px',
                    display: 'flex',
                    flexFlow: 'column'
                }}
            >
                {hasPermission('ai_smart_creator') && (
                    <UpperMenuList />
                )}
                <ProjectList/>
                <MenuList/>
            </PerfectScrollbar>
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [matchUpLg, hasPermission('ai_smart_creator')]
    );

    const container = window !== undefined ? () => window.document.body : undefined;

    const handleLogout = async () => {
        try {
            await logout();
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <Box component="nav" sx={{ flexShrink: { lg: 0 }, width: matchUpLg ? drawerWidth : 'auto' }} aria-label="mailbox folders">
            <Drawer
                container={container}
                variant={matchUpLg ? 'persistent' : 'temporary'}
                anchor="left"
                open={drawerOpen}
                onClose={handleToggleDrawer}
                sx={{
                    position: 'relative',
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        background: 'transparent',
                        color: theme.palette.text.primary,
                        borderRight: 'none',
                        [theme.breakpoints.down('lg')]: {
                            bgcolor: '#DEDBF4',
                            width: `${drawerWidth + 40}px`,
                            padding: '0 20px',
                            overflowY: 'unset'
                        }
                    }
                }}
                ModalProps={{ keepMounted: true }}
                color="inherit"
            >
                {!matchUpLg && (
                    <IconButton
                        aria-label="drawer close button"
                        sx={{
                            position: 'absolute',
                            top: '52px',
                            right: '-20px',
                            bgcolor: '#FFFFFF',
                            zIndex: 1200,
                            '&:hover': {
                                bgcolor: '#FFFFFF'
                            }
                        }}
                        onClick={handleToggleDrawer}
                    >
                        <KeyboardArrowLeftOutlined />
                    </IconButton>
                )}
                <Link component={RouterLink} to={projectId != undefined? `/w/${workspaceId}/p/${projectId}` : `/w/${workspaceId}`} sx={{ display: 'flex', justifyContent: 'center', padding: '2rem 0' }}>
                    <Box component="img" alt="Nectar Icon" src={projectLogo} sx={{ width: '200px', height: 'auto' }} />
                </Link>
                <Workspace
                    workspaceId={workspaceId}
                    handleChangeWorkspace={handleChangeWorkspace}
                />
                {drawerOpen && drawer}
                <Box sx={{ position: 'absolute', bottom: 24, left: 24, display: 'flex' }}>
                    <Box>
                        <SpeedDial
                            ariaLabel="User Action"
                            direction="up"
                            icon={
                                <Avatar
                                    sx={{ background: '#6C8982', boxShadow: 'unset', color: '#FFFFFF' }}
                                >
                                    {user?.name.split(' ')[0].split('')[0]}
                                </Avatar>
                            }
                            FabProps={{
                                sx:{ width: '40px', height: '40px', background: '#6C8982', '&:hover': { background: '#6C8982' } }
                            }}
                        >
                            <SpeedDialAction
                                key="logout"
                                icon={<Logout />}
                                tooltipTitle={<FormattedMessage id="logout" />}
                                onClick={()=> {
                                    handleLogout();
                                }}
                            />
                        </SpeedDial>
                    </Box>
                    <Box sx={{ alignSelf: 'end', marginLeft: '16px', marginBottom: '4px' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                bgcolor: '#FFFFFF4D',
                                padding: '4px 8px',
                                border: '1px solid #FFFFFF',
                                borderRadius: '12px'
                            }}
                        >
                            <Box component="img" src={tokenIcon} alt="Token Icon" sx={{ width: '20px', height: '20px' }} />
                                <Typography sx={{ display: 'flex', alignItems: 'center', fontSize: '16px', fontWeight: 600, fontFamily: 'Inter', color: '#5D6F93' }}>
                                    {hasPermission('unlimited_token') ? (<IconInfinity />) : tokenCount} Tokens
                                </Typography>
                        </Box>
                    </Box>
                </Box>
            </Drawer>
        </Box>
    );
};

Sidebar.propTypes = {
    window: PropTypes.object
};

export default memo(Sidebar);
